import React, { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { optimizeCustomerPicture, validateCustomerPicture } from "../../../util/customerPicture";
import { Alert, btnPrimary, btnSecondary } from "./ui";
import CustomerAvatar from "./CustomerAvatar";

export default function CustomerPictureInput({ customer, botId, file, removed, onChange, onBusyChange }) {
  const [preview, setPreview] = useState("");
  const [source, setSource] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageValid, setImageValid] = useState(false);
  const request = useRef(0);
  useEffect(() => {
    if (!file) { setPreview(""); return undefined; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    if (!source) { setSourceUrl(""); return undefined; }
    const url = URL.createObjectURL(source);
    setSourceUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [source]);
  useEffect(() => () => { request.current += 1; }, []);
  const choose = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    setError("");
    try {
      validateCustomerPicture(selected);
      setSource(selected); setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); setImageValid(false);
      onBusyChange(true);
    } catch (err) {
      setError(err.message || "Could not read picture.");
    }
  };
  const finishCrop = async () => {
    if (!area || !imageValid || busy) return;
    const current = ++request.current;
    setBusy(true); setError("");
    try {
      const optimized = await optimizeCustomerPicture(source, area);
      if (current !== request.current) return;
      onChange(optimized, false);
      setSource(null); onBusyChange(false);
    } catch (err) {
      if (current === request.current) setError(err.message || "Could not process picture.");
    } finally {
      if (current === request.current) setBusy(false);
    }
  };
  const cancelCrop = () => { setSource(null); setError(""); onBusyChange(false); };
  const hasPicture = file || (!removed && customer?.profile_picture_file_id);
  return <section className="space-y-3" aria-label="Customer profile picture">
    <div className="flex items-center gap-3">
      {preview ? <img src={preview} alt="Selected customer avatar" className="h-16 w-16 rounded-full object-cover" />
        : <CustomerAvatar customer={removed ? { name: customer?.name } : customer} botId={botId} className="h-16 w-16" />}
      <div>
        <p className="text-xs font-semibold text-slate-600">Profile picture (optional)</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <label className={`${btnSecondary} relative cursor-pointer ${busy ? "opacity-50" : ""}`}>
            Take photo
            <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={busy}
              aria-label="Take photo" onChange={choose} className="sr-only" />
          </label>
          <label className={`${btnSecondary} relative cursor-pointer ${busy ? "opacity-50" : ""}`}>
            Choose from library
            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy}
              aria-label="Choose from library" onChange={choose} className="sr-only" />
          </label>
        </div>
        <p className="mt-1 text-xs text-slate-500">Take a new photo or choose an image, position the face, then crop it. Stored privately as one optimized image.</p>
      </div>
    </div>
    {sourceUrl && <div className="space-y-3" aria-label="Crop profile picture">
      <p className="text-sm text-slate-600">Drag to position the face. Use zoom or arrow keys in the crop area to adjust. The circular view previews your avatar; a square image is saved.</p>
      <div className="relative h-72 overflow-hidden rounded-xl bg-slate-900">
        <Cropper key={sourceUrl} image={sourceUrl} crop={crop} zoom={zoom} aspect={1} cropShape="round"
          onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, pixels) => setArea(pixels)}
          onMediaLoaded={({ naturalWidth, naturalHeight }) => {
            if (!naturalWidth || !naturalHeight || naturalWidth * naturalHeight > 25_000_000) {
              setImageValid(false); setError("Choose a picture no larger than 25 megapixels.");
            } else setImageValid(true);
          }}
          mediaProps={{ onError: () => { setImageValid(false); setError("Could not read this picture. Choose a valid image."); } }}
          zoomWithScroll={!busy} keyboardStep={busy ? 0 : 1} />
      </div>
      <label className="block text-sm font-semibold text-slate-700">Zoom
        <input type="range" min="1" max="3" step="0.05" value={zoom} disabled={busy}
          onChange={(e) => setZoom(Number(e.target.value))} className="mt-2 w-full" />
      </label>
      <div className="flex gap-2">
        <button type="button" className={btnPrimary} disabled={busy || !area || !imageValid} onClick={finishCrop}>{busy ? "Preparing picture..." : "Use cropped picture"}</button>
        <button type="button" className={btnSecondary} disabled={busy} onClick={cancelCrop}>Cancel crop</button>
      </div>
    </div>}
    {!source && hasPicture && <button type="button" className={btnSecondary} disabled={busy} onClick={() => { onChange(null, true); setError(""); }}>Remove picture</button>}
    <Alert>{error}</Alert>
  </section>;
}
